import { type Variants } from "framer-motion";

// --- Animation Variants ---
export const staggerContainer: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
};

export const fadeInUp: Variants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 100, damping: 20 },
    },
};

export const portfolioItemVariant = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.9 },
};

// --- Component Types ---

export interface WorkHeroData {
    title: string;
    boldTitle: string;
    subtitle: string;
}

export type PortfolioCategory =
    | "ALL"
    | "SAAS"
    | "WEBSITES"
    | "MOBILE APPS"
    | "BRANDING"
    | "WEB APPS"
    | "UX DISCOVERY"
    | "MVP"
    | "AI" // Added AI since several projects are AI specific
    | "HEALTHCARE";

export interface PortfolioItem {
    id: string | number;
    slug: string;
    title: string;
    description: string;
    imageUrl: string;
    tags: string[];
    categories: PortfolioCategory[]; // An item can be in multiple categories
    fullDescription: string;
    challenge: string;
    solution: string;
    link: string;
}

export interface CtaData {
    title: string;
    subtitle: string;
    buttonText: string;
    buttonHref: string;
}

// --- Page Data ---

export const workHeroData: WorkHeroData = {
    title: "the Woltrio Design's",
    boldTitle: "Incredible Showcase",
    subtitle:
        "From Ideas to Experiences, here's a bit of this and a bit of that.",
};

export const portfolioCategories: PortfolioCategory[] = [
    "ALL",
    "SAAS",
    "WEBSITES",
    "WEB APPS",
    "AI",
    "HEALTHCARE",
    "MOBILE APPS",
    "BRANDING",
];

export const allPortfolioItems: PortfolioItem[] = [
    {
        id: 10,
        title: "Acolide - Booking & Business Suite",
        description:
            "Multi-location platform for salons, spas, and clinics — covering bookings, payments, inventory, HR, accounting, and marketing campaigns from one system. Contact us to build a platform that runs your whole operation, not just your calendar.",
        imageUrl: "/assets/acolide.png",
        tags: ["GraphQL", "Node.js", "MongoDB", "Microservices"],
        categories: ["SAAS", "WEB APPS"],
        fullDescription:
            "Acolide gives multi-location service businesses — salon chains, spa groups, clinics — one platform instead of five disconnected ones. Booking, payments, inventory, staff, accounting, and marketing all run through a single login, whether you're managing one site or fifty.",
        challenge:
            "Multi-location service businesses typically stitch together a booking app, a separate POS, a payments processor, and a marketing tool that don't talk to each other — so a client's history, inventory levels, and loyalty status look different depending on which system a staff member happens to be looking at.",
        solution:
            "We built Acolide as a set of independent services — booking, payments, point-of-sale and inventory, and marketing — unified behind a single GraphQL gateway that merges them into one API, so the frontend never has to know they're separate systems underneath. Appointments carry encrypted client data end-to-end, payments run through Square and Stripe, and the marketing side handles SMS and email campaigns with its own CRM — all updating in real time across every location a business runs.",
        link: "",
        slug: "acolide-ai-ehr-clinic-management-saas",
    },
    {
        id: 9,
        title: "LinkWell — HIPAA-Compliant AI Scribe & Closed-Loop Physician Referral System",
        description:
            "An enterprise-grade, HIPAA-compliant closed-loop physician referral and ambient clinical intelligence platform with real-time AthenaHealth EHR integration, automated SOAP note generation, and prior-authorization pipeline.",
        imageUrl: "/assets/linkwell.png",
        tags: [
            "FastAPI",
            "Python 3.11",
            "React 19",
            "OpenAI GPT-4o",
            "Whisper",
            "AthenaHealth FHIR API",
            "MongoDB Atlas",
            "HIPAA Compliance",
        ],
        categories: ["HEALTHCARE", "AI", "WEB APPS"],
        fullDescription:
            "LinkWell is a comprehensive, end-to-end healthcare platform designed to modernize the fragmented physician referral lifecycle and eliminate clinical documentation burnout. Powered by OpenAI GPT-4o and Whisper speech transcription with speaker diarization, LinkWell captures ambient patient-clinician encounters to generate structured, auditable SOAP notes and automated CPT/ICD-10 coding. The platform deeply integrates with AthenaHealth's FHIR API across 15 clinical data tiers (including panel-level lab results, imaging reports, medications, and allergies). LinkWell connects primary care physicians (PCPs), specialists, clinical staff, and patients via dedicated, role-tailored portals—streamlining specialist matching, appointment booking, and complex prior authorization PDF generation.",
        challenge:
            "Medical referral workflows across outpatient clinics are plagued by severe fragmentation: over 33% of patient referrals in the US never result in a specialist visit due to communication breakdowns, lost faxes, and opaque scheduling. At the same time, clinicians spend up to two hours on administrative electronic health record (EHR) charting for every hour of direct patient care. Navigating disparate insurance prior authorization requirements, manually transcribing SOAP notes, and extracting multi-sectional historical records from EHRs create clinical burnout and substantial delays in critical patient interventions.",
        solution:
            "LinkWell solves these bottlenecks with a unified, HIPAA-compliant architecture. By combining ambient audio transcription with speaker diarization and GPT-4o intelligence, LinkWell generates accurate, physician-style-tuned SOAP notes and smart CPT/ICD-10 code recommendations in real-time. LinkWell establishes a closed-loop referral network that synchronizes bi-directionally with AthenaHealth EHR via FHIR endpoints, providing multi-role portals (Patient, PCP, Specialist, Staff, Admin) with live appointment self-scheduling, automated prior-authorization PDF generation, and strict end-to-end security (PHI redaction, audit logging, rate limiting, and session security).",
        link: "https://linkwell.ai",
        slug: "linkwell-physician-referral-system",
    },
    {
        id: 8,
        title: "Grace Family - Patient Care Portal",
        description:
            "A digital front door for a real urgent care clinic — patients register and verify their ID online, book a visit or join one over video, get prescriptions sent straight to their pharmacy, and pay their bill, all without a single phone call. Contact us to give your practice the same walk-in-to-checkout experience.",
        imageUrl: "/assets/gracefamily.png",
        tags: ["Next.js", "FastAPI", "PostgreSQL", "Telehealth"],
        categories: ["HEALTHCARE", "SAAS"],
        fullDescription:
            "Grace Family and Urgent Care wanted patients to walk in — or log in — and be seen without friction. We designed the entire visit as a single digital thread: sign up, get verified, see a provider, walk away with a prescription and a bill, all in one place.",
        challenge:
            "Urgent care runs on speed. Every extra form, phone call, or fax machine between a patient walking in and getting treated costs the practice time it can't spare — and patients who came in sick don't want to spend that time waiting in a queue of paperwork.",
        solution:
            "We built the patient side of the visit as one continuous flow: sign-up and ID verification happen before anyone sets foot in the clinic, appointments can be booked in person or over video through Google Meet, prescriptions are checked against a live drug catalog and sent electronically, and the bill is settled without a separate portal or a mailed invoice. Every step is encrypted and logged behind the scenes, so the experience stays simple for patients while the practice stays compliant.",
        link: "",
        slug: "grace-family---patient-care-portal",
    },
    {
        id: 7,
        title: "Curealog - Hospital Management Platform",
        description:
            "Multi-hospital EMR and practice management platform covering patient records, appointment and waitlist scheduling, e-prescribing, lab and billing workflows, and secure patient-provider messaging. Contact us to build your own end-to-end healthcare management system.",
        imageUrl: "/assets/curealog.png",
        tags: ["Next.js", "FastAPI", "PostgreSQL", "Healthcare"],
        categories: ["WEB APPS", "SAAS"],
        fullDescription:
            "Curealog is a multi-hospital EMR and practice management platform built for clinics and hospital networks running several facilities under one system. It unifies patient records, scheduling, clinical documentation, billing, and insurance claims into a single role-based platform for admins, doctors, staff, and patients.",
        challenge:
            "Growing hospital networks often run on a patchwork of disconnected tools — separate systems for scheduling, records, and billing — which slows down staff, delays insurance claims, and makes it hard to get a clear operational view across multiple facilities.",
        solution:
            "We built a unified, role-based platform that manages everything from patient intake and appointment scheduling to clinical documentation, e-prescribing, lab and imaging orders, and full revenue cycle management. Real-time messaging, AI-assisted patient summaries, and HL7/FHIR-ready data exchange keep the system connected to patients and existing healthcare infrastructure, while field-level encryption, audit logging, and multi-factor authentication align it with HIPAA-level security expectations.",
        link: "",
        slug: "curealog---hospital-management-platform",
    },
    {
        id: 1,
        title: "Vine - University LMS",
        description:
            "Comprehensive university learning management system featuring course management, student progress tracking, automated grading, and interactive learning tools. Contact us to build your custom LMS solution.",
        imageUrl: "/assets/vine.png",
        tags: ["Next.js", "Node.js", "AdonisJS", "Docker"],
        categories: ["WEB APPS", "SAAS"],
        fullDescription:
            "Vine is a state-of-the-art Learning Management System (LMS) tailored for modern universities. It bridges the gap between administration, faculty, and students by providing a unified platform for course management, grading, and communication.",
        challenge:
            "Universities often struggle with fragmented systems where student data, course materials, and grades are scattered across different platforms. This leads to administrative inefficiencies and a poor user experience for students.",
        solution:
            "We built a centralized, cloud-native platform using Next.js and AdonisJS. Vine integrates real-time notifications, automated grading workflows, and a responsive student portal, resulting in a 40% reduction in administrative overhead.",
        link: "https://vinelms.com/",
        slug: "vine---university-lms",
    },
    {
        id: 2,
        title: "MarketIntent - Data Automation",
        description:
            "Advanced data automation platform that streamlines market research workflows, automates data collection, and provides actionable insights. Get started with intelligent automation for your business.",
        imageUrl: "/assets/market.png",
        tags: ["Next.js", "Puppeteer", "FastAPI", "PM2", "Automation"],
        categories: ["WEB APPS", "SAAS", "AI"],
        fullDescription:
            "MarketIntent is a powerful data intelligence tool that automates the extraction of competitive market data. It empowers businesses to make informed decisions by providing real-time insights into market trends and competitor pricing.",
        challenge:
            "Manual data collection is time-consuming and prone to errors. Businesses needed a way to gather vast amounts of market data quickly and accurately without dedicating significant human resources.",
        solution:
            "We developed a scalable scraping engine using Puppeteer and FastAPI. The system manages thousands of concurrent scraping jobs via PM2, cleaning and structuring the data automatically for immediate analysis.",
        link: "https://marketintent.example.com",
        slug: "marketintent---data-automation",
    },
    {
        id: 3,
        title: "TrendSetGo - AI Trends Analyzer",
        description:
            "AI-powered trends analysis platform that identifies emerging market patterns, predicts consumer behavior, and delivers actionable business intelligence. Discover how AI can transform your market analysis.",
        imageUrl: "/assets/trendsetgo.png",
        tags: ["React", "FastAPI", "Apify", "Docker"],
        categories: ["WEB APPS", "SAAS", "AI"],
        fullDescription:
            "TrendSetGo utilizes advanced AI algorithms to analyze social media and market data, identifying emerging trends before they go mainstream. It's designed for marketers and product developers looking for the next big thing.",
        challenge:
            "Identifying trends early is difficult due to the sheer volume of noise on social media. Traditional analytics tools are often reactive rather than predictive.",
        solution:
            "By integrating Apollo data with custom AI models served via FastAPI, TrendSetGo filters out the noise. It provides a predictive score for potential trends, allowing users to capitalize on opportunities weeks before competitors.",
        link: "https://trendsetgo.example.com",
        slug: "trendsetgo---ai-trends-analyzer",
    },
    {
        id: 4,
        title: "Daisy.ai - Intelligent EMR",
        description:
            "HIPAA-compliant intelligent EMR system with AI-assisted clinical documentation, automated workflows, and seamless HL7/FHIR Interoperability. Start building your custom healthcare solution today.",
        imageUrl: "/assets/daisy.png",
        tags: ["Next.js", "FastAPI", "Healthcare", "AI"],
        categories: ["WEB APPS", "SAAS", "AI"],
        fullDescription:
            "Daisy.ai reimagines the Electronic Medical Record system by embedding AI assistance directly into the clinician's workflow. It helps with documentation, diagnosis suggestions, and patient follow-ups.",
        challenge:
            "Physicians spend nearly 50% of their time on paperwork, leading to burnout and less time for patient care. Existing EMRs are clunky and unintuitive.",
        solution:
            "We created a streamlined, voice-enabled EMR interface. The AI backend listens to patient consultations (with consent) and automatically drafts clinical notes, which the doctor simply reviews and signs, saving hours of daily administrative work.",
        link: "https://daisy-ai.example.com",
        slug: "daisyai---intelligent-emr",
    },
    {
        id: 5,
        title: "BillingVista - Billing Solutions",
        description:
            "Comprehensive billing and invoicing platform with automated payment processing, subscription management, and financial reporting. Contact us to streamline your billing operations.",
        imageUrl: "/assets/billing.png",
        tags: ["Next.js", "Web Design", "UI/UX"],
        categories: ["WEBSITES", "BRANDING"],
        fullDescription:
            "BillingVista is a fintech platform designed to simplify complex billing and subscription management for SaaS companies. It offers a sleek, user-friendly dashboard for managing invoices and payments.",
        challenge:
            "Complex billing logic (proration, usage-based billing) often results in confusing invoices and customer churn. The client needed a UI that made these complex concepts feel simple.",
        solution:
            "We focused heavily on UX/UI design, creating a visual language that clarifies billing cycles. The new dashboard allows users to visualize their usage and costs in real-time, increasing transparency and trust.",
        link: "https://www.billingvista.com/",
        slug: "billingvista---billing-solutions",
    },
    {
        id: 6,
        title: "AelaPro - Job Notifications",
        description:
            "Real-time job notification system with intelligent matching algorithms, customizable alerts, and multi-channel delivery. Hire us to build your custom notification platform.",
        imageUrl: "/assets/aela.png",
        tags: ["Next.js", "FastAPI", "Automation"],
        categories: ["WEB APPS", "SAAS"],
        fullDescription:
            "AelaPro is a career acceleration tool that monitors job boards 24/7. It instantly notifies job seekers when a role matching their specific criteria is posted, giving them a first-mover advantage.",
        challenge:
            "In a competitive job market, timing is everything. Manually refreshing job boards is inefficient and leads to missed opportunities.",
        solution:
            "We built a real-time notification engine that aggregates listings from major job sites. Users set granular filters, and the system pushes alerts via email or SMS within seconds of a new posting.",
        link: "https://aelapro.example.com",
        slug: "aelapro---job-notifications",
    },
];

// Re-using the blue CTA from the solutions images
export const workCtaData: CtaData = {
    title: "Stunning Design, Ready When You Need Them",
    subtitle: "at an affordable rate to help scale your business.",
    buttonText: "CONTACT US",
    buttonHref: "/inquiry",
};

export function getCaseStudiesBySlugs(slugs: string[]): PortfolioItem[] {
    return slugs
        .map((slug) => allPortfolioItems.find((p) => p.slug === slug))
        .filter((p): p is PortfolioItem => Boolean(p));
}